/**
 * Product reels: snap scroll, mobile fullscreen viewer, scroll chaining on embeds.
 */
const MOBILE_MQ = window.matchMedia('(max-width: 749px)');

/** @type {string | undefined} */
let viewerCloseLabel;

class ReelsMobileViewer {
  /** @type {HTMLElement | null} */
  static #root = null;

  /** @type {HTMLElement | null} */
  static #viewport = null;

  /** @type {HTMLElement | null} */
  static #track = null;

  /** @type {IntersectionObserver | null} */
  static #observer = null;

  /** @type {ProductReelsFeed | null} */
  static #sourceFeed = null;

  static #ensure() {
    if (this.#root) return this.#root;

    const root = document.createElement('div');
    root.className = 'reels-mobile-viewer';
    root.hidden = true;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.innerHTML = `
      <button type="button" class="reels-mobile-viewer__close" data-reels-viewer-close aria-label="">×</button>
      <div class="reels-mobile-viewer__viewport" data-reels-viewer-viewport tabindex="-1">
        <div class="reels-mobile-viewer__track" data-reels-viewer-track></div>
      </div>
    `;

    document.body.appendChild(root);

    this.#root = root;
    this.#viewport = root.querySelector('[data-reels-viewer-viewport]');
    this.#track = root.querySelector('[data-reels-viewer-track]');

    const closeBtn = root.querySelector('[data-reels-viewer-close]');
    closeBtn?.addEventListener('click', () => this.close());
    root.addEventListener('click', (e) => {
      if (e.target === root) this.close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.#root && !this.#root.hidden) this.close();
    });

    return root;
  }

  /**
   * @param {ProductReelsFeed} feed
   * @param {HTMLElement} slideEl
   */
  static open(feed, slideEl) {
    const root = this.#ensure();
    const viewport = this.#viewport;
    const track = this.#track;
    if (!viewport || !track) return;

    const slides = [...feed.querySelectorAll('.product-reel-slide')];
    const index = Math.max(0, slides.indexOf(slideEl));

    track.replaceChildren();
    slides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      const link = clone.querySelector('.product-reel-slide__link');
      if (link) link.remove();
      track.appendChild(clone);
    });

    const closeBtn = root.querySelector('[data-reels-viewer-close]');
    const closeLabel =
      feed.dataset.reelsCloseLabel || viewerCloseLabel || 'Close reels';
    if (closeBtn) closeBtn.setAttribute('aria-label', closeLabel);

    this.#sourceFeed = feed;
    root.hidden = false;
    document.body.classList.add('reels-mobile-viewer--open');
    closeBtn?.focus({ preventScroll: true });

    this.#wireVideos(viewport);

    requestAnimationFrame(() => {
      const target = track.children[index];
      if (target instanceof HTMLElement) {
        viewport.scrollTop = target.offsetTop;
      }
      const video = target?.querySelector('video');
      video?.play().catch(() => {});
    });
  }

  static close() {
    if (!this.#root || this.#root.hidden) return;

    this.#track?.querySelectorAll('video').forEach((v) => v.pause());
    this.#observer?.disconnect();
    this.#observer = null;

    this.#root.hidden = true;
    document.body.classList.remove('reels-mobile-viewer--open');
    this.#track?.replaceChildren([]);
    this.#sourceFeed = null;
  }

  /**
   * @param {HTMLElement} viewport
   */
  static #wireVideos(viewport) {
    this.#observer?.disconnect();
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = entry.target.querySelector('video');
          if (!video) continue;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.45) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      },
      { root: viewport, threshold: [0, 0.45, 0.75] }
    );

    viewport.querySelectorAll('.product-reel-slide').forEach((slide) => {
      this.#observer?.observe(slide);
    });
  }
}

class ProductReelsFeed extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {boolean} */
  #loading = false;

  /** @type {MediaQueryList} */
  #mq = window.matchMedia('(min-width: 750px)');

  /** @type {((e: MediaQueryListEvent) => void) | null} */
  #onMqChange = null;

  connectedCallback() {
    this.#viewport = this.querySelector('.product-reels__viewport');
    this.#track = this.querySelector('.product-reels__track');
    this.#loader = this.querySelector('.product-reels__loader');

    if (!this.#viewport || !this.#track) return;

    if (!viewerCloseLabel && this.dataset.reelsCloseLabel) {
      viewerCloseLabel = this.dataset.reelsCloseLabel;
    }

    this.addEventListener('reels-fab-opened', this.#onFabOpened);
    this.#setupVideoObserver();
    this.#viewport.addEventListener('scroll', this.#onViewportScroll, { passive: true });

    this.#onMqChange = () => {
      this.#onLayoutModeChange();
      this.#updateMobileEmbedMode();
    };
    this.#mq.addEventListener('change', this.#onMqChange);
    MOBILE_MQ.addEventListener('change', this.#onMqChange);

    this.#updateMobileEmbedMode();
    if (this.#usesVerticalViewport()) {
      this.#setupScrollChaining();
    }
    this.addEventListener('click', this.#onFeedClick);
  }

  #onFabOpened = () => {
    this.#observer?.disconnect();
    this.#setupVideoObserver();
  };

  disconnectedCallback() {
    this.removeEventListener('reels-fab-opened', this.#onFabOpened);
    this.removeEventListener('click', this.#onFeedClick);
    this.#observer?.disconnect();
    this.#viewport?.removeEventListener('scroll', this.#onViewportScroll);
    if (this.#onMqChange) {
      this.#mq.removeEventListener('change', this.#onMqChange);
      MOBILE_MQ.removeEventListener('change', this.#onMqChange);
    }
    this.#scrollChainAbort?.abort();
  }

  #viewport;
  #track;
  #loader;

  /** @type {AbortController | null} */
  #scrollChainAbort = null;

  /** @type {number} */
  #touchStartY = 0;

  /** @type {number} */
  #touchLastY = 0;

  #isDesktopGrid() {
    return (
      this.classList.contains('product-reels--desktop-grid-wide') &&
      this.dataset.desktopLayout === 'grid' &&
      this.#mq.matches
    );
  }

  #usesVerticalViewport() {
    return !this.#isDesktopGrid();
  }

  #isFabDrawer() {
    return this.classList.contains('product-reels--fab-drawer');
  }

  /** Compact / homepage embed — tap opens Instagram-style fullscreen */
  #shouldOpenMobileViewer() {
    if (!MOBILE_MQ.matches) return false;
    if (this.#isFabDrawer()) return false;
    if (this.classList.contains('product-reels--full')) return false;
    return this.classList.contains('product-reels--compact');
  }

  #updateMobileEmbedMode() {
    const embed =
      MOBILE_MQ.matches && this.#usesVerticalViewport() && !this.#isFabDrawer();
    this.classList.toggle('product-reels--mobile-embed', embed);
    this.dataset.mobileTapExpand = this.#shouldOpenMobileViewer() ? 'true' : 'false';
  }

  #onLayoutModeChange = () => {
    this.#observer?.disconnect();
    this.#setupVideoObserver();
    this.#updateMobileEmbedMode();
  };

  #onFeedClick = (e) => {
    if (!this.#shouldOpenMobileViewer()) return;

    if (e.target.closest('.product-reel-slide__cta')) return;

    const slide = e.target.closest('.product-reel-slide');
    if (!slide || !this.contains(slide)) return;

    e.preventDefault();
    e.stopPropagation();
    ReelsMobileViewer.open(this, slide);
  };

  #setupScrollChaining() {
    if (this.#isFabDrawer()) return;

    this.#scrollChainAbort?.abort();
    this.#scrollChainAbort = new AbortController();
    const { signal } = this.#scrollChainAbort;
    const vp = this.#viewport;
    if (!vp) return;

    const atBottom = () => vp.scrollTop + vp.clientHeight >= vp.scrollHeight - 4;
    const atTop = () => vp.scrollTop <= 4;

    vp.addEventListener(
      'wheel',
      (e) => {
        if (this.#isDesktopGrid()) return;
        if (atBottom() && e.deltaY > 0) {
          window.scrollBy({ top: e.deltaY, behavior: 'auto' });
        } else if (atTop() && e.deltaY < 0) {
          window.scrollBy({ top: e.deltaY, behavior: 'auto' });
        }
      },
      { passive: true, signal }
    );

    vp.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length !== 1) return;
        this.#touchStartY = e.touches[0].clientY;
        this.#touchLastY = this.#touchStartY;
      },
      { passive: true, signal }
    );

    vp.addEventListener(
      'touchmove',
      (e) => {
        if (this.#isDesktopGrid() || e.touches.length !== 1) return;
        const y = e.touches[0].clientY;
        const delta = this.#touchLastY - y;
        this.#touchLastY = y;

        if (atBottom() && delta > 0) {
          window.scrollBy({ top: delta, behavior: 'auto' });
        } else if (atTop() && delta < 0) {
          window.scrollBy({ top: delta, behavior: 'auto' });
        }
      },
      { passive: true, signal }
    );
  }

  #setupVideoObserver() {
    this.#observer?.disconnect();
    const root = this.#isDesktopGrid() ? null : this.#viewport;
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const slide = entry.target;
          const video = slide.querySelector('video');
          if (!video) continue;

          if (entry.isIntersecting && entry.intersectionRatio >= 0.45) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      },
      { root, rootMargin: '0px', threshold: [0, 0.45, 0.75] }
    );

    this.querySelectorAll('.product-reel-slide').forEach((slide) => {
      this.#observer?.observe(slide);
    });
  }

  #onViewportScroll = () => {
    this.#tryLoadMore(() => {
      const { scrollTop, clientHeight, scrollHeight } = this.#viewport;
      return scrollTop + clientHeight >= scrollHeight - clientHeight * 0.5;
    });
  };

  /** @param {() => boolean} nearEnd */
  #tryLoadMore(nearEnd) {
    if (this.dataset.enableInfinite !== 'true') return;
    if (this.#loading) return;
    if (!nearEnd()) return;

    const current = parseInt(this.dataset.currentPage || '1', 10);
    const total = parseInt(this.dataset.totalPages || '1', 10);
    if (current >= total) return;

    this.#loadNextPage(current + 1);
  }

  /**
   * @param {number} page
   */
  async #loadNextPage(page) {
    const sectionId = (this.dataset.sectionId || '').trim();
    if (!sectionId) return;

    this.#loading = true;
    if (this.#loader) this.#loader.hidden = false;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('page', String(page));

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error('Failed to load reels');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const sourceTrack = doc.querySelector(
        `[data-section-id="${sectionId}"] [data-reels-track]`
      );

      if (!sourceTrack) return;

      const slides = sourceTrack.querySelectorAll('.product-reel-slide');
      slides.forEach((slide) => {
        this.#track.appendChild(slide);
        this.#observer?.observe(slide);
      });

      this.dataset.currentPage = String(page);
    } catch (e) {
      console.warn('Product reels: could not load more', e);
    } finally {
      this.#loading = false;
      if (this.#loader) this.#loader.hidden = true;
    }
  }
}

if (!customElements.get('product-reels-feed')) {
  customElements.define('product-reels-feed', ProductReelsFeed);
}
